import { useCallback, useEffect, useRef, useState } from 'react';
import type { NotificationRecipientsDoc } from '@kawai/shared';
import { fetchNotificationRecipients } from '../repositories/notificationRecipients.js';

export type NotificationRecipientsStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface UseNotificationRecipientsResult {
  status: NotificationRecipientsStatus;
  /** `null` = doc 未作成（名簿なし）。 */
  doc: NotificationRecipientsDoc | null;
  /** 保存後に画面側から最新 doc を差し込む（再 fetch せずに整合させる）。 */
  setDoc: (doc: NotificationRecipientsDoc) => void;
}

/**
 * N-15b: `notification_recipients/{tenantId}` を読む hook（useBillingCharges と同じ形）。
 */
export function useNotificationRecipients(
  tenantId: string | null,
  fetcher: (
    id: string,
  ) => Promise<NotificationRecipientsDoc | null> = fetchNotificationRecipients,
): UseNotificationRecipientsResult {
  const fetcherRef = useRef(fetcher);
  const [state, setState] = useState<{
    status: NotificationRecipientsStatus;
    doc: NotificationRecipientsDoc | null;
  }>({ status: tenantId ? 'loading' : 'idle', doc: null });

  useEffect(() => {
    if (!tenantId) {
      setState({ status: 'idle', doc: null });
      return;
    }
    let cancelled = false;
    setState((prev) => ({ status: 'loading', doc: prev.doc }));
    fetcherRef
      .current(tenantId)
      .then((doc) => {
        if (!cancelled) setState({ status: 'ready', doc });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.error('[notificationRecipients] fetch failed:', err);
        setState({ status: 'error', doc: null });
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  const setDoc = useCallback((doc: NotificationRecipientsDoc) => {
    setState({ status: 'ready', doc });
  }, []);

  return { ...state, setDoc };
}
